import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { styled, css } from "styled-components";
import { IfPermitted, Timestamp, LinkContainer } from "components/common";
import SearchLink from "views/logic/search/SearchLink";
import { createElasticsearchQueryString } from "views/logic/queries/Query";
import { Row, Col, Table, Button, Badge, BootstrapModalConfirm, ButtonToolbar } from "components/bootstrap";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
const RetrievedStatusBadge = styled(Badge)(
  ({ theme }) => css`
    margin-left: ${theme.spacings.xxs};
  `
);
const StyledTbody = styled.tbody`
  ${({ $showIndices, theme }) => $showIndices && css`
      border-left: 4px solid ${theme.colors.variant.info};
    `}
  & > tr:is(:first-child) {
    cursor: pointer;
  }
`;
const StyledTable = styled(Table)(
  ({ theme }) => css`
    margin-top: ${theme.spacings.sm};
    .table-hover > tbody > tr:hover {
      background: none;
    }
  `
);
const StyledDetailTr = styled.tr(
  ({ theme }) => css`
    & > td {
      padding-left: ${theme.spacings.lg} !important;
      padding-top: ${theme.spacings.sm} !important;
    }
  `
);
const DataLakeRetrievalTableEntry = ({ retrievalOperation, index, onDeleteOperation }) => {
  const { t } = useTranslation("enterprise_data_lake");
  const [showIndices, setShowIndices] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const sendTelemetry = useSendTelemetry();
  const {
    retrieved_at,
    partially_retrieved,
    retrieved_documents,
    index_failures,
    timestamp_from,
    timestamp_to,
    restore_indices
  } = retrievalOperation;
  const onConfirmDelete = () => {
    onDeleteOperation(retrievalOperation.id);
  };
  const handleDelete = (event) => {
    event.stopPropagation();
    setShowConfirmDelete(true);
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.RETRIEVAL_OPERATION_DELETE_OPENED, {});
  };
  const getIndicesQueryString = (restoreIndices) => restoreIndices.reduce((acc, indice, currentIndex, arr) => {
    if (currentIndex + 1 === arr.length) {
      return `${acc}_index:${indice.index_name}`;
    }
    return `${acc}_index:${indice.index_name} OR `;
  }, "");
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledTbody, { $showIndices: showIndices }, /* @__PURE__ */ React.createElement("tr", { key: retrieved_at, onClick: () => setShowIndices(!showIndices) }, /* @__PURE__ */ React.createElement("td", null, index + 1), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: retrieved_at })), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(RetrievedStatusBadge, { bsStyle: partially_retrieved ? "warning" : "default" }, partially_retrieved ? t("entry.partially_restored") : t("entry.fully_restored"))), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: timestamp_from })), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: timestamp_to })), /* @__PURE__ */ React.createElement("td", null, retrieved_documents), /* @__PURE__ */ React.createElement("td", null, index_failures), /* @__PURE__ */ React.createElement("td", null, restore_indices.length), /* @__PURE__ */ React.createElement("td", { "aria-label": t("entry.actions") }, /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(
    LinkContainer,
    {
      key: `received-messages-${index}`,
      to: SearchLink.builder().query(createElasticsearchQueryString(getIndicesQueryString(restore_indices))).timerange({
        type: "absolute",
        from: timestamp_from,
        to: timestamp_to
      }).build().toURL()
    },
    /* @__PURE__ */ React.createElement(
      Button,
      {
        bsSize: "xsmall",
        onClick: () => {
          sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.RETRIEVAL_OPERATION_VIEW_CLICKED, {});
        }
      },
      t("entry.show_messages")
    )
  ), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: ["data_lake_archive:delete"] }, /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall", bsStyle: "danger", onClick: handleDelete }, t("entry.delete")))))), showIndices && /* @__PURE__ */ React.createElement(StyledDetailTr, null, /* @__PURE__ */ React.createElement("td", { colSpan: 9, "aria-label": t("entry.details_aria") }, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 6 }, /* @__PURE__ */ React.createElement("p", null, t("entry.indices_description")), /* @__PURE__ */ React.createElement(StyledTable, { condensed: true }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, t("entry.indices_header")), /* @__PURE__ */ React.createElement("th", null, t("entry.action_header")))), /* @__PURE__ */ React.createElement("tbody", null, restore_indices.map((indice) => /* @__PURE__ */ React.createElement("tr", { key: indice.index_name }, /* @__PURE__ */ React.createElement("td", null, indice.index_name), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(
    LinkContainer,
    {
      key: `received-messages-${indice.index_name}`,
      to: SearchLink.builder().query(createElasticsearchQueryString(`_index:${indice.index_name}`)).timerange({
        type: "absolute",
        from: timestamp_from,
        to: timestamp_to
      }).build().toURL()
    },
    /* @__PURE__ */ React.createElement(
      Button,
      {
        bsSize: "xsmall",
        "aria-label": t("entry.show_messages_for_index", { index: indice.index_name })
      },
      t("entry.show_messages")
    )
  ))))))))))), showConfirmDelete && /* @__PURE__ */ React.createElement(
    BootstrapModalConfirm,
    {
      showModal: showConfirmDelete,
      onConfirm: () => onConfirmDelete(),
      onCancel: () => setShowConfirmDelete(false),
      title: t("entry.confirm_title")
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "entry.delete_confirm_text",
        values: { count: restore_indices == null ? void 0 : restore_indices.length },
        components: {
          timestamp: /* @__PURE__ */ React.createElement("b", null, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: retrieved_at })),
          bold: /* @__PURE__ */ React.createElement("b", null)
        }
      }
    )), /* @__PURE__ */ React.createElement("p", null, t("entry.continue_question")))
  ));
};
export default DataLakeRetrievalTableEntry;
